import { Clock, BookOpen, Wifi, MessageSquare, Terminal, Monitor, Smartphone, LifeBuoy, AlertTriangle } from 'lucide-react'
import ModuleLayout from '../components/ModuleLayout'
import PagedLesson from '../components/PagedLesson'
import CopyBlock from '../components/CopyBlock'
import PlatformPicker, { ForPlatform } from '../components/PlatformPicker'
import { usePlatform } from '../lib/platform'

/**
 * Module 17 — Getting Set Up. Optional: plenty of people will already have
 * this working, and anyone who gets stuck should stop rather than fight it.
 *
 * NO QUESTIONS, deliberately (Jack, 2026-08-20). Quizzing install steps tests
 * whether somebody read a page they were about to follow anyway, and the
 * desktop apps now have written guides to follow instead. PagedLesson treats a
 * lesson with no questions as unscored: it completes, but writes no score — see
 * the `unscored` branch there, and note that a stored 0 would otherwise land in
 * the trainee's average as if they had failed a real quiz.
 *
 * DESKTOP APPS ARE OUT OF SCOPE. Each has its own Confluence guide, linked on
 * page 2, and this module does not restate their steps — two copies of an
 * install procedure diverge, and the older one is always the one somebody
 * finds. Those pages are login-gated, so nothing here paraphrases their
 * contents; the only desktop facts kept are the ones sourced from the support
 * channel (the installer's Enter-key default, and Antigravity refusing to log
 * in from inside a `wa` session).
 *
 * SOURCE OF TRUTH is the pinned install message in #support-workspace-automation
 * (hiveempire.slack.com/archives/C0AG7K2QP8D/p1773285529350659), not the repo's
 * install/README.md. The Confluence onboarding page explicitly defers to it and
 * warns its own copy may be stale.
 *
 * This module departs from the pinned message in exactly one place, because
 * the message is written Mac-first: it says `source ~/.zshrc` for everyone,
 * but WSL Ubuntu has no .zshrc and does have .bashrc.
 * [P workspace-automation] install.sh:1789-1797 picks .zshrc if it exists and
 * falls back to .bashrc, so on Windows it writes to .bashrc — which is
 * therefore what Windows users must source.
 *
 * On the Node version: install/README.md insists on node@22 and warns that
 * Node 23+ breaks native modules. That warning is out of date.
 * [P workspace-automation] install.sh:475 detects Node 23+ and switches to the
 * built-in node:sqlite so no native module is needed, and the only hard floor
 * is Node 18, at line 468 of the same file. The pinned guide's plain
 * `brew install node` is correct, so this module carries no caveat about it —
 * a warning about a solved problem just makes a nervous audience hesitate.
 * The README is the thing that wants fixing.
 */

const CLONE_CMD = 'git clone https://github.com/finderau/workspace-automation.git ~/dev/workspace-automation'

function Intro({ pageCount }) {
  return (
    <section className="rounded-xl border-2 border-indigo-200 bg-indigo-50/50 p-6">
      <h2 className="mb-2 text-2xl font-bold text-slate-800">Getting Claude on your machine</h2>
      <p className="mb-3 text-slate-700">
        This module walks you through installing the terminal version of Workspace Automation. It
        works on both Mac and Windows, and you'll only be shown the steps for the machine you're
        actually on. If you'd rather have a desktop app, the next page links the guide for each —
        follow that instead.
      </p>
      <p className="mb-3 text-slate-700">
        It's <strong>optional</strong>. If Claude already runs when you type{' '}
        <code className="rounded bg-white px-1 py-0.5 text-xs">wa</code>, skip it. And if you get
        stuck, stop — there's a page at the end on where to get help. Nobody expects you to fight
        an installer.
      </p>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-4 w-4 text-indigo-500" />
          20–30 minutes, mostly waiting
        </span>
        <span className="inline-flex items-center gap-1.5">
          <BookOpen className="h-4 w-4 text-indigo-500" />
          {pageCount} steps · nothing to answer
        </span>
      </div>
    </section>
  )
}

function Step({ n, children }) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-600">
        {n}
      </span>
      <div className="min-w-0 flex-1 space-y-2">{children}</div>
    </div>
  )
}

function buildPages(platform, setPlatform) {
  return [
    {
      title: 'First: do you even need this?',
      content: (
        <>
          <p>
            There are four places you can use Workspace Automation from. Wally needs nothing
            installed. The desktop apps each have their own guide. The terminal is what this module
            walks through — and mobile is your phone borrowing that setup, so it needs the terminal
            working first.
          </p>
          <div className="space-y-3">
            {[
              {
                Icon: MessageSquare,
                name: 'Wally, in Slack',
                best: 'Quick questions, summarising a thread, checking your calendar — while you\'re already in Slack.',
                how: 'Mention @Wally in a channel, or DM the Wally app. Nothing to install, nothing to set up — it is already running.',
              },
              {
                Icon: Terminal,
                name: 'The terminal (wa)',
                best: 'Heavier work: spreadsheets, bulk file jobs, long research sessions. This is what the rest of this course uses.',
                how: 'Type wa in a terminal. Needs the setup in this module — and it is the same setup mobile depends on.',
              },
              {
                Icon: Monitor,
                name: 'The desktop apps',
                best: 'The same work as the terminal, in a window, if a command line is not how you like to think.',
                how: 'Claude Code and Antigravity both come as desktop apps, and each has its own install guide — linked on the next page. Follow the guide rather than the steps in this module.',
              },
              {
                Icon: Smartphone,
                name: 'Mobile',
                best: 'Checking something quickly when you\'re away from your desk.',
                how: 'Run wa --setup on your laptop for a QR code. Not a separate install — it hands your phone access to the terminal setup above, so that has to exist first.',
              },
            ].map((option) => (
              <div key={option.name} className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="mb-1 flex items-center gap-2">
                  <option.Icon className="h-4 w-4 text-indigo-600" />
                  <h3 className="font-semibold text-slate-800">{option.name}</h3>
                </div>
                <p className="text-sm text-slate-600">{option.best}</p>
                <p className="mt-1 text-sm text-slate-500">{option.how}</p>
              </div>
            ))}
          </div>
          <p>
            If all you want is to ask quick questions in Slack, Wally is already there and you can
            stop after this page. Carry on if you want the terminal version.
          </p>
        </>
      ),
    },

    {
      title: 'Which one — and where the instructions live',
      content: (
        <>
          <p>
            Two separate choices hide inside "get me set up", and they get run together:{' '}
            <strong>where</strong> you use it — terminal, desktop app, Slack, phone — and{' '}
            <strong>which model</strong> answers you. They are independent, and all of them reach
            the same Finder tools.
          </p>

          <div className="rounded-xl border-2 border-indigo-200 bg-indigo-50 p-4">
            <h3 className="font-semibold text-indigo-900">
              Want the desktop app? Follow its guide, not this module
            </h3>
            <p className="mt-1 text-sm text-indigo-800">
              Both desktop apps now have a written install-and-setup guide. If a window suits you
              better than a command line, open the one for your app and work through it there:
            </p>
            <ul className="mt-2 space-y-2">
              {[
                {
                  name: 'Claude Code — desktop app',
                  href: 'https://finder.atlassian.net/wiki/spaces/FKB/pages/4519198727/Get+started+with+Claude+Code+Desktop+App+install+setup',
                },
                {
                  name: 'Antigravity (agy)',
                  href: 'https://finder.atlassian.net/wiki/spaces/FKB/pages/4418076709/Get+started+with+Antigravity+agy+install+setup',
                },
              ].map((guide) => (
                <li key={guide.href}>
                  <a
                    href={guide.href}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-800 underline"
                  >
                    <Monitor className="h-4 w-4 shrink-0" />
                    {guide.name}
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-sm text-indigo-800">
              This module deliberately doesn't repeat their steps. A second copy of an install
              guide only goes stale and starts contradicting the first one.
            </p>
          </div>

          <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4">
            <h3 className="font-semibold text-amber-900">
              The one to get right at install time: which model
            </h3>
            <p className="mt-1 text-sm text-amber-800">
              The terminal installer asks which agent you want as your default and offers four —{' '}
              <strong>Antigravity</strong> (Google), <strong>Claude Code</strong> (Anthropic),
              Codex, and Gemini CLI. <strong>Antigravity is what it picks if you just press
              Enter</strong>, and the rest of this course is written for Claude.
            </p>
            <p className="mt-2 text-sm text-amber-800">
              So choose <strong>Claude Code</strong> when it asks — or, if you've already installed
              and got something that doesn't look like Claude, run{' '}
              <code className="rounded bg-white/70 px-1 py-0.5 text-xs">wa --claude</code>. The
              reverse is <code className="rounded bg-white/70 px-1 py-0.5 text-xs">wa --agy</code>.
              Both are wired to the same gateway and the same skills, so this is a choice of model,
              not a choice of capability.
            </p>
            <p className="mt-2 text-sm text-amber-800">
              One quirk worth knowing before you go hunting for it: you can't log in to Antigravity
              from inside a <code className="rounded bg-white/70 px-1 py-0.5 text-xs">wa</code>{' '}
              session. Open a fresh terminal window instead.
            </p>
          </div>

          <p>
            Everything after this page is the <strong>terminal</strong> path — the one the rest of
            this course uses, and the one mobile access is built on top of.
          </p>
        </>
      ),
    },

    {
      title: 'Before you start',
      content: (
        <>
          <div className="rounded-lg border-l-4 border-amber-400 bg-amber-50 py-3 pl-4 pr-3">
            <div className="flex items-start gap-2">
              <Wifi className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-sm text-amber-900">
                <strong>Turn your VPN off</strong> before you begin, and leave it off until you're
                finished. It interferes with the installer, and the failures it causes look like
                completely unrelated problems.
              </p>
            </div>
          </div>
          <div className="rounded-xl border-2 border-indigo-200 bg-indigo-50 p-4">
            <h3 className="font-semibold text-indigo-900">
              Ask for access first — it is three things, and they are granted by people
            </h3>
            <p className="mt-1 text-sm text-indigo-800">
              Post once in <strong>#support-workspace-automation</strong> asking for all three,
              because a human grants each of them and waiting on the second one after finishing the
              install is a miserable way to spend an afternoon:
            </p>
            <ol className="mt-2 ml-4 list-decimal space-y-1 text-sm text-indigo-800">
              <li><strong>finderau GitHub organisation</strong> access — without it the download step fails.</li>
              <li><strong>Claude / Antigravity access</strong> — the models themselves.</li>
              <li>
                <strong>MCP Gateway whitelisting</strong> — this is the one people forget. Every
                Finder tool reaches you through the gateway, and direct connections were switched
                off company-wide in July. Without it you get a working terminal that cannot see a
                single spreadsheet, email or Slack message.
              </li>
            </ol>
            <p className="mt-2 text-sm text-indigo-800">
              Say which you need and include your GitHub username — that is what someone will ask
              for otherwise.
            </p>
          </div>

          <p>You also need:</p>
          <ul className="ml-1 space-y-1.5">
            {[
              'Your Finder Google account — that\'s what you\'ll sign in with.',
              'Admin rights on your own machine, to install software.',
              'About half an hour, most of it waiting for downloads.',
            ].map((x) => (
              <li key={x} className="flex items-start gap-2.5">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
                <span>{x}</span>
              </li>
            ))}
          </ul>
          <p>
            One last thing before the steps: the commands differ depending on your machine, so tell
            us which you're on. You can change this later if you pick wrong.
          </p>
          <PlatformPicker platform={platform} onChange={setPlatform} />
        </>
      ),
    },

    {
      title: 'Step 1 — Open a terminal',
      content: (
        <>
          <p>
            The terminal is a window where you type commands instead of clicking. You don't need to
            understand it — you just need one open.
          </p>
          <ForPlatform
            platform={platform}
            mac={
              <div className="space-y-3">
                <Step n={1}>
                  <p>
                    Press <kbd className="rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-xs">⌘</kbd>{' '}
                    +{' '}
                    <kbd className="rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-xs">Space</kbd>{' '}
                    and type <strong>terminal</strong>, then hit Enter.
                  </p>
                </Step>
                <Step n={2}>
                  <p>A window with a text prompt opens. That's it — you're ready for step 2.</p>
                  <p className="text-sm text-slate-500">
                    If you'd prefer a nicer one, iTerm2 is a popular alternative, but the built-in
                    Terminal is completely fine.
                  </p>
                </Step>
              </div>
            }
            windows={
              <div className="space-y-3">
                <p>
                  Windows needs one extra piece first: a Linux environment called WSL2. Windows
                  installs it for you.
                </p>
                <Step n={1}>
                  <p>
                    Open the <strong>Start menu</strong>, search for <strong>PowerShell</strong>,
                    right-click it and choose <strong>Run as administrator</strong>.
                  </p>
                </Step>
                <Step n={2}>
                  <p>Run this:</p>
                  <CopyBlock text="wsl --install" />
                </Step>
                <Step n={3}>
                  <p>
                    Restart your PC if it asks you to. If it doesn't, search for{' '}
                    <strong>Ubuntu</strong> in the Start menu and open it.
                  </p>
                </Step>
                <Step n={4}>
                  <p>
                    Ubuntu finishes setting itself up and asks you to create a username and
                    password. The username must be <strong>lowercase with no spaces</strong>.
                  </p>
                  <div className="rounded-lg border-l-4 border-amber-400 bg-amber-50 py-2.5 pl-4 pr-3 text-sm text-amber-900">
                    When you type the password, <strong>nothing appears on screen</strong> — no
                    dots, no stars. That's normal and it is not broken. Type it and press Enter.
                  </div>
                </Step>
                <Step n={5}>
                  <p>
                    You now have a Linux terminal open. Every command from here on goes in that
                    Ubuntu window, not PowerShell.
                  </p>
                </Step>
              </div>
            }
          />
        </>
      ),
    },

    {
      title: 'Step 2 — Install Homebrew',
      content: (
        <>
          <p>
            Homebrew is the tool that installs the other tools. Paste this into your terminal and
            press Enter. Skip it if you already have Homebrew.
          </p>
          <CopyBlock text={'/bin/bash -c "$(curl -fsSL https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)"'} />
          <p>
            It'll take a few minutes and print a lot of text. That's normal. It may ask for your
            computer password — again, nothing will appear as you type it.
          </p>
          <p>
            When it finishes it tells you to add itself to your PATH. That's this next bit, and it's
            different on each machine:
          </p>
          <ForPlatform
            platform={platform}
            mac={<CopyBlock text={'eval "$(/opt/homebrew/bin/brew shellenv)"'} />}
            windows={
              <div className="space-y-2">
                <CopyBlock
                  text={'eval "$(/home/linuxbrew/.linuxbrew/bin/brew shellenv)"\necho \'eval "$(/home/linuxbrew/.linuxbrew/bin/brew shellenv)"\' >> ~/.bashrc\nsource ~/.bashrc'}
                />
                <p className="text-sm text-slate-500">
                  Three commands. The middle one writes the setting into your shell config so it
                  survives closing the window.
                </p>
              </div>
            }
          />
        </>
      ),
    },

    {
      title: 'Step 3 — Install the tools and sign in to GitHub',
      content: (
        <>
          <p>Now install the three things the installer needs:</p>
          <CopyBlock text="brew install node git gh" />
          <p>Then sign in to GitHub, which is where the code lives:</p>
          <CopyBlock text="gh auth login" />
          <p>
            Answer the prompts: <strong>GitHub.com</strong> → <strong>HTTPS</strong> →{' '}
            <strong>login with a web browser</strong>. It gives you a one-time code, you press
            Enter, and it opens your browser to paste it in.
          </p>
          <p className="text-sm text-slate-500">
            This step is required because the Workspace Automation code is in a private repository.
            Without it, the next step fails.
          </p>
        </>
      ),
    },

    {
      title: 'Step 4 — Download it and run the installer',
      content: (
        <>
          <p>Download the code:</p>
          <CopyBlock text={CLONE_CMD} />
          <p>Then run the installer:</p>
          <CopyBlock text="cd ~/dev/workspace-automation/install && ./install.sh" />
          <p>
            This is the long one. It sets everything up and then opens your browser twice — once to
            authorise your <strong>Google</strong> account and once for <strong>Slack</strong>.
            Approve both; that's what lets Claude reach your spreadsheets and messages.
          </p>
          <div className="rounded-lg border-l-4 border-amber-400 bg-amber-50 py-2.5 pl-4 pr-3 text-sm text-amber-900">
            If the download fails with <strong>"Repository not found"</strong>, don't retype it —
            it's almost never a typo. There's a fix on the last page of this module.
          </div>
        </>
      ),
    },

    {
      title: 'Step 5 — Start it up',
      content: (
        <>
          <p>Load the new setup into your terminal, then start Claude:</p>
          <ForPlatform
            platform={platform}
            mac={<CopyBlock text={'source ~/.zshrc\ncd ~/dev\nwa'} />}
            windows={<CopyBlock text={'source ~/.bashrc\ncd ~/dev\nwa'} />}
          />
          <p className="text-sm text-slate-500">
            If that first line gives an error, just close the terminal and open a new one — it does
            the same job.
          </p>
          <p>
            <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">wa</code> is how you start
            Claude from now on. Not <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">claude</code>{' '}
            — the wrapper is what connects it to your Google and Slack accounts.
          </p>
          <p className="pt-1 font-semibold text-slate-800">Two optional extras</p>
          <div className="space-y-2">
            <div>
              <p className="text-sm text-slate-600">Use it from your phone — gives you a QR code:</p>
              <CopyBlock text="wa --setup" />
            </div>
            <div>
              <p className="text-sm text-slate-600">See how much of your usage allowance you've used:</p>
              <CopyBlock text="wa --claudelimits" />
            </div>
          </div>
        </>
      ),
    },

    {
      title: 'When it goes wrong',
      content: (
        <>
          <p>
            Installs fail. It is not a reflection on you, and the people in support have seen every
            one of these before.
          </p>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="mb-1 font-semibold text-slate-800">"Repository not found"</h3>
            <p className="text-sm text-slate-600">
              The most common one, and it's misleading — usually your <code>git</code> isn't using
              the GitHub login you just set up, even though <code>gh</code> is fine. Run this, then
              retry the download:
            </p>
            <div className="mt-2">
              <CopyBlock text={`gh auth setup-git\n${CLONE_CMD}`} />
            </div>
            <p className="mt-2 text-sm text-slate-500">
              If it still fails, you may not be in the finderau GitHub organisation yet — check your
              email for an invitation, and say so when you ask for help.
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="mb-1 font-semibold text-slate-800">Anything else</h3>
            <p className="text-sm text-slate-600">
              Take a screenshot of the error, <strong>crop out anything that looks like a password,
              key or token</strong>, and share it. A picture of the actual error saves far more time
              than describing it.
            </p>
          </div>

          <div className="rounded-lg border-l-4 border-indigo-300 bg-indigo-50/60 py-3 pl-4 pr-3">
            <div className="flex items-start gap-2">
              <LifeBuoy className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />
              <div className="text-sm text-slate-700">
                <p className="font-semibold text-slate-800">Where to ask</p>
                <p className="mt-1">
                  Post in <strong>#support-workspace-automation</strong> with your screenshot. It's
                  the right place — it's what the channel is for, and roughly 130 people use this
                  tool, so your problem is probably not new.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border-l-4 border-amber-400 bg-amber-50 py-3 pl-4 pr-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-sm text-amber-900">
                <strong>Give it fifteen minutes, then stop.</strong> If you're still stuck, take
                your laptop to James rather than grinding at it. This module is optional and getting
                stuck on it is not a reason to fall behind on the rest of the course — the next
                modules will still be here.
              </p>
            </div>
          </div>
        </>
      ),
    },
  ]
}

export default function ModuleGettingSetUp() {
  const [platform, setPlatform] = usePlatform()
  const pages = buildPages(platform, setPlatform)

  return (
    <ModuleLayout moduleId={17}>
      <div className="space-y-10">
        <Intro pageCount={pages.length} />
        <PagedLesson moduleId={17} pages={pages} />
      </div>
    </ModuleLayout>
  )
}
